import { forwardRef } from 'react';
import { List as MuiList, ListProps as MuiListProps } from '@mui/material';

/**
 * List component - Container for list items.
 *
 * This is a thin wrapper around Material-UI's List component that provides:
 * - Dense mode for compact lists
 * - Padding control (disablePadding)
 * - Subheader support
 * - Theme integration
 * - Full accessibility (semantic <ul> element)
 *
 * Common use cases:
 * - Navigation menus
 * - Settings lists
 * - Work item lists (WorkList pattern)
 * - Content lists with ContentListItem
 *
 * @example
 * // Basic list (WorkList pattern)
 * <List role="list" aria-label="work tasks">
 *   <ListItem>Task 1</ListItem>
 *   <ListItem>Task 2</ListItem>
 * </List>
 *
 * @example
 * // Dense list for compact display
 * <List dense>
 *   <ListItem>Item 1</ListItem>
 *   <ListItem>Item 2</ListItem>
 * </List>
 *
 * @example
 * // List with subheader
 * <List subheader={<ListSubheader>Section Title</ListSubheader>}>
 *   <ListItem>Item 1</ListItem>
 * </List>
 */
export type ListProps = MuiListProps;

export const List = forwardRef<HTMLUListElement, ListProps>((props, ref) => (
  <MuiList ref={ref} {...props} />
));

List.displayName = 'List';
